/**
 * Tax arithmetic, in one place so the POS, the API and any report agree.
 *
 * MIRRORED in client-vite/src/lib/tax.js so the New Sale screen shows exactly
 * what the server will charge. Keep the two files identical — a test fails if
 * they drift.
 *
 * Ghana charges tax in two stages: the levies (NHIL, GETFund, COVID) are
 * computed on the net base, and VAT is computed on (base + levies). A tax
 * flagged `is_compound` takes the second stage; everything else the first.
 *
 * Which taxes a product pays (first match wins):
 *   1. product.tax_exempt            -> none ("None" on the product form)
 *   2. product.tax_rate_id           -> that rate, or that group's members
 *   3. the rate/group marked default -> its taxes
 *   4. business.tax_rate             -> one "Tax" line (the pre-tax-rates setup)
 *   5. nothing
 * So an untouched product follows the business default, and an installation
 * that never opens the Tax Rates page behaves exactly as it did before.
 */

/** Money rounds to 2dp at every step, so a displayed total always re-adds. */
export const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const isActive = (t) => Number(t?.status ?? 1) === 1;
const bySortOrder = (a, b) =>
  (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0) ||
  (Number(a.id) || 0) - (Number(b.id) || 0);

/**
 * Which taxes actually apply, given a flat list of rates.
 *
 * Falls back to the single `business.tax_rate` when no tax rates have been set
 * up.
 *
 * @param {Array} taxRates rows from `tax_rates`
 * @param {number|string} fallbackRate business.tax_rate
 */
export function resolveTaxes(taxRates, fallbackRate) {
  const active = (taxRates || [])
    .filter((t) => isActive(t) && !Number(t.is_group) && Number(t.rate) > 0)
    .sort(bySortOrder);
  if (active.length) return active;

  const rate = Number(fallbackRate) || 0;
  return rate > 0
    ? [{ id: null, name: 'Tax', rate, is_compound: 0, sort_order: 0 }]
    : [];
}

/**
 * Apply [taxes] to a taxable [base], returning a per-tax breakdown and total.
 *
 * @returns {{ lines: Array<{tax_rate_id, name, rate, is_compound, sort_order, amount}>, total: number }}
 */
export function computeTaxes(base, taxes = []) {
  const taxable = Math.max(0, round2(base));
  if (!taxable || !taxes.length) return { lines: [], total: 0 };

  // Stage one: levies on the base.
  const simpleTotal = round2(
    taxes
      .filter((t) => !Number(t.is_compound))
      .reduce((sum, t) => sum + round2((taxable * Number(t.rate)) / 100), 0)
  );
  // Stage two: a compound tax (VAT) sees the base plus those levies.
  const compoundBase = round2(taxable + simpleTotal);

  const lines = taxes.map((t) => ({
    tax_rate_id: t.id ?? null,
    name: t.name,
    rate: Number(t.rate),
    is_compound: Number(t.is_compound) ? 1 : 0,
    sort_order: Number(t.sort_order) || 0,
    amount: round2(
      ((Number(t.is_compound) ? compoundBase : taxable) * Number(t.rate)) / 100
    ),
  }));

  return {
    lines,
    total: round2(lines.reduce((sum, l) => sum + l.amount, 0)),
  };
}

/**
 * The single percentage a set of taxes adds up to, compounding included.
 * Ghana (2.5 + 2.5 + 1, then 15 compound) is 21.9, not 21.
 */
export function effectiveRate(taxes = []) {
  const simple = taxes.filter((t) => !Number(t.is_compound)).reduce((s, t) => s + Number(t.rate), 0);
  const compound = taxes.filter((t) => Number(t.is_compound)).reduce((s, t) => s + Number(t.rate), 0);
  return Math.round(((1 + simple / 100) * (1 + compound / 100) - 1) * 100 * 1000) / 1000;
}

/** For a tax-inclusive price: the part of [gross] that is not tax. */
export function inclusiveBase(gross, taxes = []) {
  return round2(Number(gross) / (1 + effectiveRate(taxes) / 100));
}

/**
 * Everything the resolver needs, built once per request.
 *
 * @param {{ taxRates?: Array, groupItems?: Array<{group_id, tax_rate_id}>, businessRate?: number|string }} src
 */
export function buildTaxContext({ taxRates = [], groupItems = [], businessRate = 0 } = {}) {
  const byId = new Map((taxRates || []).map((t) => [Number(t.id), t]));
  const membersOf = new Map();
  for (const gi of groupItems || []) {
    const gid = Number(gi.group_id);
    if (!membersOf.has(gid)) membersOf.set(gid, []);
    membersOf.get(gid).push(Number(gi.tax_rate_id));
  }
  const defaultEntry = (taxRates || []).find((t) => Number(t.is_default) && isActive(t)) || null;
  return { byId, membersOf, defaultEntry, businessRate: Number(businessRate) || 0 };
}

/** A rate -> [itself]; a group -> its active member rates, in order. */
export function expandTaxEntry(entry, ctx) {
  if (!entry || !isActive(entry)) return [];
  if (!Number(entry.is_group)) return Number(entry.rate) > 0 ? [entry] : [];
  return (ctx.membersOf.get(Number(entry.id)) || [])
    .map((id) => ctx.byId.get(id))
    .filter((t) => t && isActive(t) && !Number(t.is_group) && Number(t.rate) > 0)
    .sort(bySortOrder);
}

/** The taxes one product pays — the ladder at the top of this file. */
export function taxesForProduct(product, ctx) {
  if (Number(product?.tax_exempt)) return [];

  const ownId = product?.tax_rate_id != null && product.tax_rate_id !== '' ? Number(product.tax_rate_id) : null;
  if (ownId && ctx.byId.has(ownId)) {
    // An explicit choice is honoured even when it is switched off: turning a
    // tax off stops charging it, it does not quietly swap in another one.
    return expandTaxEntry(ctx.byId.get(ownId), ctx);
  }
  if (ctx.defaultEntry) return expandTaxEntry(ctx.defaultEntry, ctx);
  return ctx.businessRate > 0
    ? [{ id: null, name: 'Tax', rate: ctx.businessRate, is_compound: 0, sort_order: 0 }]
    : [];
}

/**
 * Tax for a whole sale.
 *
 * The discount is shared across lines in proportion to their value, so an
 * exempt line absorbs its share instead of shrinking the taxed lines' base.
 * Lines paying the same taxes are pooled before rounding — with a single rate
 * this is exactly `round2((subtotal - discount) * rate / 100)`, the formula
 * sales used before tax rates existed.
 *
 * Exclusive prices get tax added on top; inclusive prices already contain it,
 * so it is carved out and the total does not grow.
 *
 * @param {{ lines: Array<{total, tax_rate_id?, tax_exempt?, tax_type?}>, discount?: number, ctx: object }} args
 */
export function computeSaleTax({ lines = [], discount = 0, ctx }) {
  const totals = lines.map((l) => Math.max(0, round2(l.total)));
  const subtotal = round2(totals.reduce((s, v) => s + v, 0));
  const disc = round2(Math.min(Math.max(0, Number(discount) || 0), subtotal));

  // Proportional shares, the last priced line taking the rounding remainder so
  // the shares add back to the discount exactly.
  const shares = totals.map(() => 0);
  if (disc > 0 && subtotal > 0) {
    let lastIdx = -1;
    totals.forEach((v, i) => { if (v > 0) lastIdx = i; });
    let given = 0;
    totals.forEach((v, i) => {
      if (i === lastIdx) return;
      shares[i] = round2((disc * v) / subtotal);
      given = round2(given + shares[i]);
    });
    if (lastIdx >= 0) shares[lastIdx] = round2(disc - given);
  }

  const pools = new Map();
  lines.forEach((line, i) => {
    const net = round2(totals[i] - shares[i]);
    const taxes = taxesForProduct(line, ctx);
    if (net <= 0 || !taxes.length) return;
    const inclusive = String(line.tax_type || '').toLowerCase() === 'inclusive';
    const key = `${inclusive ? 'i' : 'e'}|${taxes.map((t) => t.id ?? `r${t.rate}`).join(',')}`;
    if (!pools.has(key)) pools.set(key, { taxes, inclusive, gross: 0 });
    const pool = pools.get(key);
    pool.gross = round2(pool.gross + net);
  });

  const merged = new Map();
  let exclusiveTax = 0;
  let inclusiveTax = 0;
  for (const { taxes, inclusive, gross } of pools.values()) {
    const base = inclusive ? inclusiveBase(gross, taxes) : gross;
    const { lines: taxLines, total } = computeTaxes(base, taxes);
    if (inclusive && taxLines.length) {
      // Base + tax must rebuild the price the customer saw, to the pesewa.
      const residual = round2(gross - base - total);
      if (residual) taxLines[taxLines.length - 1].amount = round2(taxLines[taxLines.length - 1].amount + residual);
    }
    for (const tl of taxLines) {
      const k = `${tl.tax_rate_id ?? tl.name}|${tl.rate}|${tl.is_compound}|${inclusive ? 1 : 0}`;
      const prev = merged.get(k);
      if (prev) prev.amount = round2(prev.amount + tl.amount);
      else merged.set(k, { ...tl, is_inclusive: inclusive ? 1 : 0 });
      if (inclusive) inclusiveTax = round2(inclusiveTax + tl.amount);
      else exclusiveTax = round2(exclusiveTax + tl.amount);
    }
  }

  const taxLines = [...merged.values()]
    .filter((l) => l.amount > 0)
    .sort((a, b) => a.is_inclusive - b.is_inclusive || a.sort_order - b.sort_order);

  return {
    subtotal,
    discount: disc,
    taxLines,
    exclusiveTax,
    inclusiveTax,
    taxTotal: round2(exclusiveTax + inclusiveTax),
    total: round2(subtotal - disc + exclusiveTax),
  };
}
